import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { usePublications, Publication } from '../../contexts/PublicationsContext';
import { uploadPublicationImage } from '../../api/publications';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Loader2, X, Bold, Italic, Heading1, Heading2, FileSpreadsheet, Upload } from "lucide-react";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Badge } from "../../components/ui/badge";

interface AddPublicationProps {
  onClose: () => void;
  onSuccess: () => void;
  initialData?: Publication;
}

const AddPublication: React.FC<AddPublicationProps> = ({ onClose, onSuccess, initialData }) => {
  const { addPublication, updatePublication } = usePublications();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  // const [isUploading, setIsUploading] = useState(false); // No longer needed for immediate upload
  const [isDragging, setIsDragging] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    content: initialData?.content || '',
    excerpt: initialData?.excerpt || '',
    category: (initialData?.category as 'actualite' | 'annonce' | 'evenement' | 'formation') || 'actualite',
    imageUrl: initialData?.imageUrl || '',
    published: initialData?.published ?? true,
    tags: initialData?.tags || [] as string[]
  });

  const categories = [
    { value: 'actualite', label: 'Actualité', icon: '📢' },
    { value: 'annonce', label: 'Annonce', icon: '📋' },
    { value: 'evenement', label: 'Événement', icon: '🎉' },
    { value: 'formation', label: 'Formation', icon: '🎓' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, category: value as any }));
  };

  const handleSwitchChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, published: checked }));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !formData.tags.includes(newTag)) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
        setTagInput('');
      }
    } else if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
      // Remove last tag if backspace pressed with empty input
      setFormData(prev => ({
        ...prev,
        tags: prev.tags.slice(0, -1)
      }));
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const insertFormatting = (format: 'bold' | 'italic' | 'h1' | 'h2') => {
    const textarea = document.getElementById('content') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const selection = text.substring(start, end);
    const after = text.substring(end);

    let newText = '';
    let newCursorPos = end;

    switch (format) {
      case 'bold':
        newText = `${before}**${selection || 'Texte en gras'}**${after}`;
        newCursorPos = end + 4 + (selection ? 0 : 13); // +4 for markers, +length of default text
        if (!selection) newCursorPos = start + 2; // Position inside **
        break;
      case 'italic':
        newText = `${before}*${selection || 'Texte en italique'}*${after}`;
        newCursorPos = end + 2 + (selection ? 0 : 17);
        if (!selection) newCursorPos = start + 1;
        break;
      case 'h1':
        newText = `${before}\n# ${selection || 'Titre 1'}\n${after}`;
        newCursorPos = end + 3 + (selection ? 0 : 7);
        break;
      case 'h2':
        newText = `${before}\n## ${selection || 'Titre 2'}\n${after}`;
        newCursorPos = end + 4 + (selection ? 0 : 7);
        break;
    }

    setFormData(prev => ({ ...prev, content: newText }));

    // Defer focus to ensure React render cycle completes
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedImageFile(file);
    setFormData(prev => ({ ...prev, imageUrl: URL.createObjectURL(file) }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedExcelFile(file);
    const reader = new FileReader();
    // ... (rest of handleFileUpload using selectedExcelFile) ...
    reader.onload = (e) => {
      const data = e.target?.result;
      if (!data) return;

      try {
        if (file.name.endsWith('.csv')) {
          // Parse CSV
          Papa.parse(data as string, {
            complete: (results) => {
              if (results.data && results.data.length > 0) {
                processTableData(results.data as string[][]);
              }
            },
            skipEmptyLines: true,
          });
        } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          // Parse Excel
          const workbook = XLSX.read(data, { type: 'binary' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as string[][];
          processTableData(jsonData);
        }
      } catch (error) {
        console.error('Error parsing file:', error);
        alert('Erreur lors de la lecture du fichier. Assurez-vous qu\'il s\'agit d\'un fichier Excel ou CSV valide.');
      }
    };

    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const processTableData = (tableData: string[][]) => {
    if (tableData.length === 0) return;

    // Filter out completely empty rows
    const filteredData = tableData.filter(row =>
      row.some(cell => String(cell || '').trim() !== '')
    );

    if (filteredData.length === 0) return;

    const maxCols = Math.max(...filteredData.map(row => row.length));

    // Pad the header (first row) to match maxCols
    const headerRow = filteredData[0];
    const paddedHeader = [...headerRow, ...Array(maxCols - headerRow.length).fill('')];

    let markdownTable = '\n| ' + paddedHeader.map(cell => String(cell || '').trim() || ' ').join(' | ') + ' |\n';

    // Separator row
    markdownTable += '| ' + Array(maxCols).fill('---').join(' | ') + ' |\n';

    // Data rows (skip header)
    for (let i = 1; i < filteredData.length; i++) {
      const row = filteredData[i];
      // Pad row if it has fewer columns than maxCols
      const paddedRow = [...row, ...Array(maxCols - row.length).fill('')];
      // Ensure cells don't break the table structure (escape pipes)
      const safeRow = paddedRow.map(cell => (String(cell || '').trim() || ' ').replace(/\|/g, '\\|'));
      markdownTable += '| ' + safeRow.join(' | ') + ' |\n';
    }

    markdownTable += '\n';

    setFormData(prev => ({ ...prev, content: prev.content + markdownTable }));
    setSelectedExcelFile(null); // Updated state name
    setShowExcelModal(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    // ...
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // ...
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Check if file is Excel or CSV
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv')) {
        setSelectedExcelFile(file); // Updated state name
        // Trigger the same processing as file upload
        // ... (rest of logic same) ...
        const reader = new FileReader();
        reader.onload = (event) => {
          const data = event.target?.result;
          if (!data) return;

          try {
            if (file.name.endsWith('.csv')) {
              Papa.parse(data as string, {
                complete: (results) => {
                  if (results.data && results.data.length > 0) {
                    processTableData(results.data as string[][]);
                  }
                },
                skipEmptyLines: true,
              });
            } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
              const workbook = XLSX.read(data, { type: 'binary' });
              const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
              const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as string[][];
              processTableData(jsonData);
            }
          } catch (error) {
            console.error('Error parsing file:', error);
            alert('Erreur lors de la lecture du fichier. Assurez-vous qu\'il s\'agit d\'un fichier Excel ou CSV valide.');
          }
        };

        if (file.name.endsWith('.csv')) {
          reader.readAsText(file);
        } else {
          reader.readAsBinaryString(file);
        }
      } else {
        alert('Format de fichier non supporté. Veuillez utiliser Excel (.xlsx, .xls) ou CSV (.csv).');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let finalImageUrl = formData.imageUrl;

      // Upload image if selected
      if (selectedImageFile) {
        finalImageUrl = await uploadPublicationImage(selectedImageFile);
      }

      const excerpt = formData.excerpt || formData.content.substring(0, 150) + '...';
      const payload = {
        title: formData.title,
        content: formData.content,
        excerpt,
        category: formData.category,
        imageUrl: finalImageUrl || undefined,
        published: formData.published,
        tags: formData.tags
      };

      if (initialData) {
        await updatePublication(initialData.id, payload);
      } else {
        await addPublication(payload);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving publication:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] p-0 overflow-hidden">
        {/* ... (Header) ... */}
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{initialData ? 'Modifier la Publication' : 'Nouvelle Publication'}</DialogTitle>
          <DialogDescription>
            {initialData ? 'Modifiez les informations ci-dessous.' : 'Remplissez le formulaire ci-dessous pour créer une nouvelle publication.'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-full max-h-[calc(90vh-100px)] px-6 pb-6">

          <form onSubmit={handleSubmit} className="space-y-6 py-4" style={{ maxWidth: '100%', overflow: 'hidden' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ... (Title & Category) ... */}
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Titre de la publication"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Catégorie *</Label>
                <Select value={formData.category} onValueChange={handleSelectChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <span>{cat.icon} {cat.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="imageUrl">Image de couverture</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="text"
                    id="imageUrl"
                    name="imageUrl"
                    value={selectedImageFile ? selectedImageFile.name : formData.imageUrl}
                    onChange={handleInputChange}
                    placeholder="L'image s'affichera ici après sélection"
                    className="flex-1"
                    disabled
                  />
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      title="Choisir une image"
                    />
                    <Button type="button" variant="outline" size="icon">
                      <Upload className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* <p className="text-xs text-muted-foreground">URL ou fichier depuis votre appareil</p> */}
                {formData.imageUrl && selectedImageFile && (
                  <div className="mt-2 w-full max-h-[200px] overflow-hidden rounded-md border bg-muted flex items-center justify-center">
                    <img
                      src={formData.imageUrl}
                      alt="Aperçu"
                      className="max-h-[200px] w-auto object-contain"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="excerpt">Résumé (optionnel)</Label>
              <Textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows={3}
                placeholder="Résumé court (généré automatiquement si vide)"
              />
            </div>

            <div className="space-y-2" style={{ maxWidth: '100%', overflow: 'hidden' }}>
              <div className="flex justify-between items-center">
                <Label htmlFor="content">Contenu *</Label>
                <div className="flex items-center space-x-1 bg-muted/50 p-1 rounded-md border text-muted-foreground">
                  <button type="button" onClick={() => insertFormatting('bold')} className="p-1 hover:bg-background hover:text-foreground rounded transition-colors" title="Gras">
                    <Bold className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => insertFormatting('italic')} className="p-1 hover:bg-background hover:text-foreground rounded transition-colors" title="Italique">
                    <Italic className="h-4 w-4" />
                  </button>
                  <div className="w-px h-4 bg-border mx-1" />
                  <button type="button" onClick={() => insertFormatting('h1')} className="p-1 hover:bg-background hover:text-foreground rounded transition-colors" title="Titre 1">
                    <Heading1 className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => insertFormatting('h2')} className="p-1 hover:bg-background hover:text-foreground rounded transition-colors" title="Titre 2">
                    <Heading2 className="h-4 w-4" />
                  </button>
                  <div className="w-px h-4 bg-border mx-1" />
                  <button type="button" onClick={() => setShowExcelModal(true)} className="p-1 hover:bg-background hover:text-foreground rounded transition-colors" title="Importer depuis Excel">
                    <FileSpreadsheet className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <Textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                required
                rows={12}
                className="font-mono text-sm leading-relaxed resize-none"
                style={{ maxWidth: '100%', width: '100%', whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflowWrap: 'break-word' }}
                placeholder="Utilisez le Markdown ou la barre d'outils ci-dessus pour formater votre texte..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Tags (optionnel)</Label>
              <div className="flex flex-wrap gap-2 p-2 border rounded-md border-input bg-background focus-within:ring-1 focus-within:ring-ring focus-within:border-primary">
                {formData.tags.map((tag, index) => (
                  <Badge key={index} variant="default" className="flex items-center gap-1 pl-2 pr-1 py-1 rounded-md text-sm">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="rounded-full hover:bg-muted p-0.5 transition-colors"
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove {tag}</span>
                    </button>
                  </Badge>
                ))}
                <input
                  id="tags"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  className="flex-1 bg-transparent border-none outline-none text-sm min-w-[120px] placeholder:text-muted-foreground"
                  placeholder={formData.tags.length === 0 ? "admission, étudiants, formation (Entrée pour ajouter)" : ""}
                />
              </div>
              <p className="text-xs text-muted-foreground">Appuyez sur Entrée ou virgule pour ajouter un tag</p>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="published"
                checked={formData.published}
                onCheckedChange={handleSwitchChange}
              />
              <Label htmlFor="published">Publier immédiatement</Label>
            </div>

            {formData.title && formData.content && (
              <div className="rounded-lg border bg-muted/50 p-4 mt-6" style={{ maxWidth: '100%', width: '100%', overflow: 'hidden' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium uppercase text-muted-foreground">Aperçu du rendu</span>
                  <span className="text-xs text-muted-foreground">{new Date().toLocaleDateString('fr-FR')}</span>
                </div>
                <h4 className="text-lg font-bold mb-2">{formData.title}</h4>
                <div className="prose dark:prose-invert text-sm bg-background/50 p-3 rounded-md border max-h-[300px]" style={{ maxWidth: '100%', width: '100%', overflow: 'auto', minWidth: 0 }}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({ node, ...props }) => (
                        <div className="not-prose my-4 p-4 bg-muted/50 rounded-lg border-2 border-dashed border-muted-foreground/25 text-center">
                          <p className="text-sm text-muted-foreground">📊 Tableau détecté (visible après publication)</p>
                        </div>
                      ),
                      thead: ({ node, ...props }) => (
                        <thead className="bg-[#1e293b] text-white [&_tr]:border-b" {...props} />
                      ),
                      tbody: ({ node, ...props }) => (
                        <tbody className="[&_tr:last-child]:border-0" {...props} />
                      ),
                      tr: ({ node, ...props }) => (
                        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted even:bg-muted/20" {...props} />
                      ),
                      th: ({ node, ...props }) => (
                        <th className="h-12 px-4 text-left align-middle font-medium text-white/90 border-r last:border-r-0 border-slate-700 whitespace-nowrap" {...props} />
                      ),
                      td: ({ node, ...props }) => (
                        <td className="p-4 align-middle border-r last:border-r-0 border-muted whitespace-nowrap" {...props} />
                      ),
                    }}
                  >
                    {formData.content}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting || !formData.title || !formData.content}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Enregistrement...' : (initialData ? 'Mettre à jour' : 'Publier')}
              </Button>
            </DialogFooter>
          </form>
        </ScrollArea>
      </DialogContent>

      <Dialog open={showExcelModal} onOpenChange={setShowExcelModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Importer depuis Excel</DialogTitle>
            <DialogDescription>
              <div className="space-y-2">
                <p>Importez un fichier Excel (.xlsx, .xls) ou CSV contenant votre tableau.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-12 transition-colors ${isDragging
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-muted-foreground/50'
              }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="rounded-full bg-primary/10 p-4">
                <Upload className="h-8 w-8 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Importer des sources</h3>
                <p className="text-sm text-muted-foreground">
                  Glissez-déposez ou <label htmlFor="file-upload" className="text-primary cursor-pointer hover:underline">sélectionnez un fichier</label> à importer
                </p>
              </div>
              <Input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              {selectedExcelFile && (
                <div className="flex items-center gap-2 bg-muted px-4 py-2 rounded-md">
                  <FileSpreadsheet className="h-4 w-4" />
                  <p className="text-sm font-medium">{selectedExcelFile.name}</p>
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Types de fichiers pris en charge : Excel (.xlsx, .xls), CSV (.csv)
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExcelModal(false)}>Annuler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AddPublication;
